import React, { useEffect, useMemo, useState } from 'react';
import { DollarSign } from 'lucide-react';
import { AutocompleteSelect } from '../../../shared/components/AutocompleteSelect';
import { TextInput } from '../../../shared/components/TextInput';
import { maskCurrencyBRL, maskPhone } from '../../../shared/utils/validators';
import type { Candidate, CreateCandidatePayload } from '../api';
import {
  useAtualizarCandidato,
  useCriarCandidato,
  useVagas,
  useVincularCandidatoAVaga,
} from '../hooks';
import { validateCandidatoForm } from '../validation/recrutamentoForms';
import { CandidaturasDoCandidato } from './CandidaturasDoCandidato';
import { CidadeUfSelect } from './CidadeUfSelect';
import { apiToCurrencyInput, currencyInputToApi } from './format';
import { RecrutamentoModal } from './RecrutamentoModal';
import {
  VagaVinculadaCampos,
  cargoDaVaga,
  vagaOptionDescription,
  vagaOptionLabel,
} from './VagaVinculadaCampos';

interface CandidatoModalProps {
  open: boolean;
  /** `null` para cadastro novo. */
  candidato: Candidate | null;
  onClose: () => void;
}

/**
 * Cadastro e edição de candidato.
 *
 * O CPF só é editável no cadastro novo: é a identidade que sustenta a
 * deduplicação, e o servidor não aceita alterá-lo. Quando o CPF vem
 * mascarado (sem `recrutamento.dados-sensiveis`), o campo aparece
 * desabilitado — reenviar o valor mascarado gravaria lixo.
 *
 * A vaga pretendida é opcional e escolhida pelo ID, não pelo cargo.
 * Selecionada, ID, cargo, cliente e modalidade aparecem bloqueados (vêm
 * da vaga) e, ao salvar, o candidato é inscrito nela (origem `interno`).
 */
export function CandidatoModal({ open, candidato, onClose }: CandidatoModalProps) {
  const criar = useCriarCandidato();
  const atualizar = useAtualizarCandidato();
  const vincular = useVincularCandidatoAVaga();
  const { data: vagas } = useVagas({ status: 'publicada', limit: 100 });

  const [values, setValues] = useState<Partial<CreateCandidatePayload>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [vagaId, setVagaId] = useState('');
  // Candidato criado cuja inscrição na vaga falhou: o reenvio só tenta
  // o vínculo, em vez de recadastrar (o CPF duplicado seria recusado).
  const [criadoId, setCriadoId] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setValues(
      candidato
        ? {
            fullName: candidato.fullName,
            cpf: candidato.cpf,
            email: candidato.email,
            phone: maskPhone(candidato.phone ?? ''),
            city: candidato.city ?? '',
            expectedSalary: apiToCurrencyInput(candidato.expectedSalary),
            linkedinUrl: candidato.linkedinUrl ?? '',
          }
        : {},
    );
    setErrors({});
    setVagaId('');
    setCriadoId(null);
  }, [open, candidato]);

  const vaga = useMemo(
    () => (vagas?.data ?? []).find((item) => item.id === vagaId) ?? null,
    [vagas, vagaId],
  );

  const editando = candidato !== null;
  const loading = criar.isPending || atualizar.isPending || vincular.isPending;
  const erro =
    ((criar.error ?? atualizar.error ?? vincular.error) as Error | null)?.message ?? null;

  function setField(key: keyof CreateCandidatePayload, value: string) {
    setValues((previous) => ({ ...previous, [key]: value }));
    setErrors((previous) => ({ ...previous, [key]: '' }));
  }

  function inscreverNaVaga(candidateId: string) {
    if (!vaga) {
      onClose();
      return;
    }
    vincular.mutate(
      { candidateId, jobOpeningId: vaga.id, source: 'interno' },
      { onSuccess: onClose },
    );
  }

  function submeter() {
    // A pretensão é validada e enviada no formato da API; a máscara fica
    // só no campo.
    const normalizados = { ...values, expectedSalary: currencyInputToApi(values.expectedSalary) };
    const next = validateCandidatoForm(normalizados, { editando });
    setErrors(next as Record<string, string>);
    if (Object.keys(next).length > 0) return;

    if (criadoId) {
      inscreverNaVaga(criadoId);
      return;
    }

    const payload = {
      fullName: values.fullName!.trim(),
      email: values.email!.trim(),
      phone: values.phone!.trim(),
      city: values.city?.trim() || undefined,
      expectedSalary: normalizados.expectedSalary,
      // Cargo só entra pela vaga escolhida; sem vaga, o valor gravado fica
      // como está (campo omitido no PATCH).
      positionOfInterest: vaga ? cargoDaVaga(vaga) || undefined : undefined,
      linkedinUrl: values.linkedinUrl?.trim() || undefined,
    };

    if (editando) {
      atualizar.mutate(
        { id: candidato.id, ...payload },
        { onSuccess: () => inscreverNaVaga(candidato.id) },
      );
      return;
    }
    criar.mutate(
      { ...payload, cpf: values.cpf!.trim() },
      {
        onSuccess: (created) => {
          setCriadoId(created.id);
          inscreverNaVaga(created.id);
        },
      },
    );
  }

  return (
    <RecrutamentoModal
      open={open}
      title={editando ? 'Editar candidato' : 'Novo candidato'}
      description={
        editando && candidato?.cpfMasked
          ? 'O CPF aparece mascarado porque seu perfil não tem permissão para vê-lo por completo.'
          : undefined
      }
      submitLabel={editando ? 'Salvar' : 'Cadastrar'}
      size="lg"
      isSubmitting={loading}
      submitError={erro}
      onClose={onClose}
      onSubmit={submeter}
    >
      {editando && <CandidaturasDoCandidato candidateId={candidato.id} />}

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <TextInput
            label="Nome completo"
            value={values.fullName ?? ''}
            onChange={(event) => setField('fullName', event.target.value)}
            error={errors.fullName}
          />
        </div>
        <TextInput
          label="CPF"
          value={values.cpf ?? ''}
          disabled={editando || criadoId !== null}
          onChange={(event) => setField('cpf', event.target.value)}
          error={errors.cpf}
        />
        <TextInput
          label="Telefone"
          inputMode="tel"
          value={values.phone ?? ''}
          onChange={(event) => setField('phone', maskPhone(event.target.value))}
          error={errors.phone}
        />
        <div className="sm:col-span-2">
          <TextInput
            label="E-mail"
            type="email"
            value={values.email ?? ''}
            onChange={(event) => setField('email', event.target.value)}
            error={errors.email}
          />
        </div>
        <CidadeUfSelect value={values.city ?? ''} onChange={(value) => setField('city', value)} />

        {/*
          A escolha é da vaga, pelo ID — nunca do cargo solto: o mesmo cargo
          existe para clientes, cidades e modalidades diferentes.
        */}
        <div className="sm:col-span-2">
          <AutocompleteSelect
            label={editando ? 'Vaga pretendida (nova inscrição)' : 'Vaga pretendida'}
            options={[
              { value: '', label: 'Nenhuma vaga' },
              ...(vagas?.data ?? []).map((item) => ({
                value: item.id,
                label: vagaOptionLabel(item),
                description: vagaOptionDescription(item),
              })),
            ]}
            value={vagaId}
            onChange={setVagaId}
            disabled={criadoId !== null}
          />
        </div>
        <VagaVinculadaCampos vaga={vaga} />

        <div className="sm:col-span-2">
          <TextInput
            label="Pretensão salarial"
            inputMode="numeric"
            icon={<DollarSign className="h-4 w-4" />}
            value={values.expectedSalary ?? ''}
            onChange={(event) => setField('expectedSalary', maskCurrencyBRL(event.target.value))}
            error={errors.expectedSalary}
          />
        </div>
        <div className="sm:col-span-2">
          <TextInput
            label="LinkedIn"
            value={values.linkedinUrl ?? ''}
            onChange={(event) => setField('linkedinUrl', event.target.value)}
          />
        </div>
      </div>
    </RecrutamentoModal>
  );
}
